import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SelectInput from "../../../../components/inputs/SelectInput";

const YES = "YES", NO = "NO";
const yesNoOptions = [{ value: YES, label: "Tak" }, { value: NO, label: "Nie" }];

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const randInt = (a, b) => a + Math.floor(Math.random() * (b - a + 1));

const BASIC_DIVISORS = [2, 3, 5, 10];
const EXT_DIVISORS   = [2, 3, 4, 5, 6, 8, 9, 10];

function digitSumSteps(n) {
  const steps = [];
  let cur = String(n);
  while (cur.length > 1) {
    const s = [...cur].reduce((acc, ch) => acc + Number(ch), 0);
    steps.push(s);
    cur = String(s);
  }
  if (steps.length === 0) steps.push(Number(cur));
  return steps;
}

// pomocniczo: budujemy łańcuszek dodawania cyfr
function sumDigitsChainLatex(n) {
  const parts = [];
  let cur = n;

  while (String(cur).length > 1) {
    const digits = [...String(cur)].join(" + ");
    const s = [...String(cur)].reduce((acc, ch) => acc + Number(ch), 0);
    parts.push(`${digits} = ${s}`);
    cur = s;
  }

  if (parts.length === 0) {
    return { chain: String(n), last: n };
  }
  return { chain: parts.join(" \\rightarrow "), last: cur };
}

function genNumberWithDivisibility(divisor, wantDivisible, minDigits, maxDigits) {
  const minN = 10 ** Math.max(3, minDigits ?? 3);
  const maxN = 10 ** Math.max(3, maxDigits ?? 5) - 1;

  if (wantDivisible) {
    const kMin = Math.ceil(minN / divisor);
    const kMax = Math.floor(maxN / divisor);
    const k = randInt(kMin, kMax);
    return k * divisor;
  } else {
    let n = randInt(minN, maxN);
    if (n % divisor === 0) n = (n + 1 <= maxN) ? (n + 1) : (n - 1);
    if (divisor === 10 && n % 10 === 0) n = (n + 1 <= maxN) ? n + 1 : n - 1;
    if (divisor === 5  && n % 5 === 0)  n = (n + 1 <= maxN) ? n + 1 : n - 1;
    return n;
  }
}

export function generateTask() {
  return generateTaskWith();
}

export function generateTaskWith(overrides = {}) {
  const variant = overrides.variant ?? "basic";
  const pool = (variant === "ext") ? EXT_DIVISORS : BASIC_DIVISORS;
  const divisor = pick(pool);
  const wantDivisible = Math.random() < 0.5;
  const minDigits = (divisor === 4 || divisor === 8) ? 4 : 3;
  const maxDigits = 5;
  const number = genNumberWithDivisibility(divisor, wantDivisible, minDigits, maxDigits);

  return {
    type: "DivisibilityYesNoTask",
    data: { number, divisor, shouldYes: wantDivisible, variant }
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div className="flex items-center gap-3 flex-wrap">
        <span>
          Czy liczba <InlineMath math={String(data.number)} /> jest podzielna przez{" "}
          <InlineMath math={String(data.divisor)} />?
        </span>
      </div>
    </div>
  );
}


export function renderInput({ data }, userAnswer, onChange, disabled) {
  const value = typeof userAnswer === "string" ? userAnswer : "";
  const set = (e) => onChange?.(e?.target?.value ?? "");
  return (
    <div className="mt-2">
      <SelectInput
        value={value}
        onChange={set}
        disabled={disabled}
        options={yesNoOptions}
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (userAnswer !== YES && userAnswer !== NO) return false;
  return (userAnswer === YES) === data.shouldYes;
}

export function renderUserAnswer(userAnswer) {
  if (userAnswer !== YES && userAnswer !== NO) return <i>(brak odpowiedzi)</i>;
  return <span>{userAnswer === YES ? "Tak" : "Nie"}</span>;
}

export function renderAnswer({ data }) {
  return <b>{data.shouldYes ? "Tak" : "Nie"}</b>;
}

export function renderExplanation({ data }) {
  const n = data.number;
  const d = data.divisor;

  const last1 = n % 10;
  const last2 = n % 100;
  const last3 = n % 1000;
  const sumOnce = [...String(n)].reduce((s, ch) => s + Number(ch), 0);
  const steps = digitSumSteps(n);
  const lastStep = steps[steps.length - 1];

  const ruleText = (() => {
    switch (d) {
      case 2:  return "ostatnia cyfra jest parzysta";
      case 3:  return "suma cyfr liczby jest podzielna przez 3 (można ją redukować aż zostanie 3, 6 lub 9)";
      case 4:  return "dwie ostatnie cyfry tworzą liczbę podzielną przez 4";
      case 5:  return "ostatnia cyfra to 0 lub 5";
      case 6:  return "liczba jest jednocześnie podzielna przez 2 i przez 3";
      case 8:  return "trzy ostatnie cyfry tworzą liczbę podzielną przez 8";
      case 9:  return "suma cyfr liczby jest podzielna przez 9 (po redukcji otrzymujemy 9)";
      case 10: return "ostatnia cyfra to 0";
      default: return "";
    }
  })();

  const chainLatex = (chain, verdict) =>
    <InlineMath math={`${chain} \\Rightarrow ${verdict}`} />;

  const lineFor3 = () => {
    const { chain, last } = sumDigitsChainLatex(n);
    const verdict =
      (last === 3 || last === 6 || last === 9)
        ? `\\text{podzielna przez 3, bo ostateczna suma }${last}\\in\\{3,6,9\\}`
        : `\\text{nie jest podzielna przez 3, bo ostateczna suma }${last}\\notin\\{3,6,9\\}`;
    return chainLatex(chain, verdict);
  };

  const lineFor9 = () => {
    const { chain, last } = sumDigitsChainLatex(n);
    const verdict =
      (last === 9)
        ? `\\text{podzielna przez 9, bo ostateczna suma cyfr to }9`
        : `\\text{nie jest podzielna przez 9, bo ostateczna suma cyfr to }${last}\\neq 9`;
    return chainLatex(chain, verdict);
  };

  const checkLine = (() => {
    switch (d) {
      case 2:
        return (
          <InlineMath
            math={`\\text{ostatnia cyfra: }${last1} \\Rightarrow ${
              last1 % 2 === 0 ? "\\text{parzysta}" : "\\text{nieparzysta}"
            }`}
          />
        );
      case 3:
        return lineFor3();
      case 4:
        return (
          <InlineMath
            math={`\\text{ostatnie 2 cyfry: }${last2} \\Rightarrow ${
              last2 % 4 === 0 ? "\\text{podzielne przez 4}" : "\\text{niepodzielne przez 4}"
            }`}
          />
        );
      case 5:
        return (
          <InlineMath
            math={`\\text{ostatnia cyfra: }${last1} \\Rightarrow ${
              last1 === 0 || last1 === 5 ? "0/5" : "\\text{inna}"
            } `}
          />
        );
      case 6: {
        const even = (last1 % 2 === 0) ? "\\text{parzysta}" : "\\text{nieparzysta}";
        const { chain, last } = sumDigitsChainLatex(n);
        const by3 =
          (last === 3 || last === 6 || last === 9)
            ? `\\text{ostateczna suma }${last}\\in\\{3,6,9\\}`
            : `\\text{ostateczna suma }${last}\\notin\\{3,6,9\\}`;
        return (
          <InlineMath
            math={`\\text{warunek }2\\text{ i }3:\\;\\text{ostatnia cyfra: }${last1}\\;(${even}),\\;\\text{suma cyfr: }${chain} \\Rightarrow ${by3}`}
          />
        );
      }
      case 8:
        return (
          <InlineMath
            math={`\\text{ostatnie 3 cyfry: }${last3} \\Rightarrow ${
              last3 % 8 === 0 ? "\\text{podzielne przez 8}" : "\\text{niepodzielne przez 8}"
            }`}
          />
        );
      case 9:
        return lineFor9();
      case 10:
        return (
          <InlineMath
            math={`\\text{ostatnia cyfra: }${last1} \\Rightarrow ${
              last1 === 0 ? "0" : "\\neq 0"
            }`}
          />
        );
      default:
        return null;
    }
  })();

  const result = data.shouldYes ? "Tak" : "Nie";

  return (
    <div style={{ display: "grid", rowGap: 8 }}>
      <p>
        <b>Krok 1:</b> Zasada podzielności dla{" "}
        <InlineMath math={String(d)} />: {ruleText}.
      </p>

      {/* Wyśrodkowane "Sprawdzenie dla ..." z liczbą w InlineMath */}
      <div style={{ textAlign: "center", margin: "8px 0" }}>
        Sprawdzenie dla <InlineMath math={String(n)} />
      </div>

      <ul>
        <li>{checkLine}</li>
      </ul>

      <p>
        <b>Wniosek:</b> <b>{result}</b>.
      </p>
    </div>
  );
}
